import config from '@/config';
import { EDITION_ROOM_MEASUREMENTS_CM_BY_SIZE, FRAME_DEPTH_CM } from '@/pages/artwork/limited-edition-room-preview/constants';
import { MUSEUM_LABEL_HEIGHT_CM, MUSEUM_LABEL_WIDTH_CM } from '@/pages/artwork/limited-edition-room-preview/museumLabel';
import { getArtworkLimitedEditionOptions, isLimitedEditionAvailable } from '@/pages/artwork/utils/artworkView.selectors';
import { getPreferredLocale } from '@/services/locale.service';

function getImageRatio(artwork) {
  const imageData = artwork?.artwork_media?.artwork_main_picture?.image_original_data;
  return Number(imageData?.aspect_ratio)
    || (Number(imageData?.width) > 0 && Number(imageData?.height) > 0
      ? Number(imageData.width) / Number(imageData.height) : null);
}

export function getOriginalRoomViewSource(artwork) {
  const size = artwork?.artwork_about?.artwork_size;
  const medium = String(artwork?.artwork_about?.artwork_medium ?? '');
  const width = Number(size?.length);
  const height = Number(size?.height);
  const unit = String(size?.unit ?? '').trim().toLowerCase();
  const metresPerUnit = unit === 'cm' ? 0.01 : (['in', 'inch', 'inches'].includes(unit) ? 0.0254 : null);
  const mainImage = artwork?.artwork_media?.artwork_main_picture;
  const imageId = mainImage?.image_id;
  const imageData = mainImage?.image_original_data;
  const status = artwork?.artwork_status;
  const imageRatio = Number(imageData?.aspect_ratio)
    || (Number(imageData?.width) > 0 && Number(imageData?.height) > 0
      ? Number(imageData.width) / Number(imageData.height) : null);
  const slug = artwork?.slug;
  const recordedRatio = width / height;
  const matchesRecordedOrientation = !imageRatio || Math.abs(imageRatio / recordedRatio - 1) <= 0.05;
  const matchesRotatedOrientation = imageRatio && Math.abs(imageRatio * recordedRatio - 1) <= 0.05;

  if (!metresPerUnit || !Number.isFinite(width) || !Number.isFinite(height)
    || width <= 0 || height <= 0 || width * metresPerUnit > 10 || height * metresPerUnit > 10
    || !imageId || !slug
    || ['queued', 'unavailable', 'sold_admin_only'].includes(status)
    || artwork?.artwork_flags?.withdrawn === true
    || artwork?.artwork_about?.artwork_gallery?.gallery_closed === true
    || (!matchesRecordedOrientation && !matchesRotatedOrientation)
    || /\b(sculpture|ceramics?|installation|assemblage|statue|pottery|three-dimensional|3d)\b/i.test(medium)) {
    return null;
  }

  const version = `${imageId}:${width}:${height}:${unit}`;
  const baseUrl = `${config.apis.api.url}/artwork/${encodeURIComponent(slug)}/room-model`;
  const rotateDimensions = !matchesRecordedOrientation && matchesRotatedOrientation;
  return {
    width: rotateDimensions ? height : width,
    height: rotateDimensions ? width : height,
    unit,
    imageId,
    glbUrl: `${baseUrl}.glb?v=${encodeURIComponent(version)}`,
    usdzUrl: `${baseUrl}.usdz?v=${encodeURIComponent(version)}`,
  };
}

export function getLimitedEditionRoomViewSource(artwork, selection) {
  const sizeKey = selection?.size;
  const frame = selection?.frame;
  const label = selection?.label;
  if (typeof sizeKey !== 'string' || !/^[a-zA-Z0-9_-]{1,40}$/.test(sizeKey)
    || !['none', 'black', 'white'].includes(frame)
    || !['none', 'white'].includes(label)) return null;

  const options = getArtworkLimitedEditionOptions(artwork);
  const measurements = Object.prototype.hasOwnProperty.call(EDITION_ROOM_MEASUREMENTS_CM_BY_SIZE, sizeKey)
    ? EDITION_ROOM_MEASUREMENTS_CM_BY_SIZE[sizeKey]
    : null;
  if ((frame !== 'none' || label !== 'none') && !measurements) return null;
  const option = Object.prototype.hasOwnProperty.call(options, sizeKey) ? options[sizeKey] : null;
  const width = Number(option?.width);
  const height = Number(option?.height);
  const imageId = artwork?.artwork_media?.artwork_main_picture?.image_id;
  const slug = artwork?.slug;
  const status = artwork?.artwork_status;
  if (!option || !isLimitedEditionAvailable(option)
    || !Number.isFinite(width) || !Number.isFinite(height)
    || width <= 0 || height <= 0 || width > 1000 || height > 1000
    || !imageId || !slug
    || ['queued', 'unavailable', 'sold_admin_only'].includes(status)
    || artwork?.artwork_flags?.withdrawn === true
    || artwork?.artwork_about?.artwork_gallery?.gallery_closed === true) return null;

  const ratio = getImageRatio(artwork);
  const recordedRatio = width / height;
  const matchesOrientation = !ratio || Math.abs(ratio / recordedRatio - 1) <= 0.05;
  const matchesRotated = ratio && Math.abs(ratio * recordedRatio - 1) <= 0.05;
  if (!matchesOrientation && !matchesRotated) return null;

  const matVersion = frame === 'none' ? 'none' : `${measurements.matWidth}cm`;
  const locale = getPreferredLocale();
  const frameVersion = frame === 'none' ? 'none' : `${measurements.frameFaceWidth}x${FRAME_DEPTH_CM}cm-${frame}-painted-ash-v1`;
  const labelGapVersion = label === 'none' ? 'none' : `${measurements.labelGap}cm`;
  const query = new URLSearchParams({ size: sizeKey, frame, label, lang: locale, v: `${imageId}:${width}:${height}:mat-${matVersion}-frame-${frameVersion}-gap-${labelGapVersion}-label-content-v2-${MUSEUM_LABEL_WIDTH_CM}x${MUSEUM_LABEL_HEIGHT_CM}-bottom` });
  const baseUrl = `${config.apis.api.url}/artwork/${encodeURIComponent(slug)}/room-model`;
  return {
    size: sizeKey,
    frame,
    label,
    width: matchesOrientation ? width : height,
    height: matchesOrientation ? height : width,
    unit: 'cm',
    glbUrl: `${baseUrl}.glb?${query}`,
    usdzUrl: `${baseUrl}.usdz?${query}`,
  };
}

export function buildLimitedEditionRoomShareUrl(origin, slug, selection) {
  const query = new URLSearchParams({
    room_view: 'limited_edition',
    room_size: selection.size,
    room_frame: selection.frame,
    room_label: selection.label,
  });
  return `${origin}/artwork/${encodeURIComponent(slug)}?${query}`;
}

export function getRoomViewPlatform() {
  if (typeof navigator === 'undefined') return 'desktop';
  const agent = navigator.userAgent || '';
  if (/android/i.test(agent)) return 'android';
  if (/iPhone|iPad|iPod/i.test(agent) || (/Macintosh/i.test(agent) && navigator.maxTouchPoints > 1)) return 'ios';
  return 'desktop';
}

export function buildAndroidRoomIntent(source, fallbackUrl, title) {
  const params = new URLSearchParams({
    file: source.glbUrl,
    mode: 'ar_only',
    resizable: 'false',
    enable_vertical_placement: 'true',
    title: title || 'Artwork',
  });
  return `intent://arvr.google.com/scene-viewer/1.0?${params.toString()}#Intent;scheme=https;package=com.google.ar.core;action=android.intent.action.VIEW;S.browser_fallback_url=${encodeURIComponent(fallbackUrl)};end;`;
}
